/**
 * Follow-Up Success Page
 *
 * Confirmation page after successful submission.
 */

import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useFollowUp } from '../context/FollowUpContext';
import { useTenant } from '../context/TenantContext';
import ShareButtons from '../components/ShareButtons';
import defaultLogo from '../assets/logo.png';
import '../styles/formPage.css';
import '../styles/followup.css';

const FollowUpSuccess = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { tenant } = useTenant();
  const { submissionInfo, clearAll, userEmail } = useFollowUp();

  // Get the logo to display - use tenant logo if available, otherwise default
  const logoToDisplay = tenant?.logoUrl || defaultLogo;

  const { changes, patientName, isNewUser } = location.state || {};

  // Prevent back navigation from success page
  useEffect(() => {
    // Replace current history entry to prevent going back
    window.history.pushState(null, '', window.location.href);

    const handlePopState = () => {
      // Push state again to prevent back navigation
      window.history.pushState(null, '', window.location.href);
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  const handleStartNew = () => {
    clearAll();
    navigate('/');
  };

  return (
    <div>
      {/* Logo */}
      <div className="logo-img d-flex-center">
        <div style={{ textAlign: 'center' }}>
          <img src={logoToDisplay} alt={tenant?.brandName || 'Logo'} style={{ width: '200px', height: 'auto', objectFit: 'contain', marginTop: '10px' }} />
        </div>
      </div>

      {/* Form Container */}
      <div className="form-container narrow">
        <div className="step-container" style={{ textAlign: 'center' }}>
          {/* Success Icon */}
          <div style={{
            width: '80px',
            height: '80px',
            margin: '0 auto 25px',
            background: 'linear-gradient(135deg, #28a745 0%, #20c997 100%)',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <svg
              width="40"
              height="40"
              viewBox="0 0 24 24"
              fill="none"
              stroke="white"
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="20 6 9 17 4 12"></polyline>
            </svg>
          </div>

          {/* Success Message */}
          <div style={{ marginBottom: '30px' }}>
            <h2 className="step-title">
              Follow-up submitted successfully!
            </h2>
          </div>

          {/* Submitted timestamp only */}
          <div style={{
            background: '#f8f9fa',
            borderRadius: '8px',
            padding: '16px 20px',
            marginBottom: '30px',
            textAlign: 'center'
          }}>
            <span style={{ color: '#6c757d', fontSize: '14px' }}>
              Submitted at {new Date().toLocaleString()}
            </span>
          </div>

          {/* Action Buttons */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', alignItems: 'center' }}>
            <button
              type="button"
              className="btn next"
              onClick={handleStartNew}
              style={{ minWidth: '200px' }}
            >
              Done
            </button>

            {/* M-12: Next Patient button in proxy/staff mode */}
            {(() => {
              try {
                const proxyInfo = sessionStorage.getItem('proxy_staff_info');
                if (proxyInfo) {
                  return (
                    <button
                      type="button"
                      onClick={() => {
                        // Clear form state but keep proxy info
                        sessionStorage.removeItem('followup_form_state');
                        window.location.href = '/?proxy=staff';
                      }}
                      style={{
                        padding: '10px 24px', background: '#f59e0b', color: '#fff',
                        border: 'none', borderRadius: '6px', fontWeight: 600,
                        fontSize: '14px', cursor: 'pointer', fontFamily: 'inherit',
                        minWidth: '200px',
                      }}
                    >
                      Start New Patient
                    </button>
                  );
                }
              } catch { /* not in proxy mode */ }
              return null;
            })()}
          </div>

          {/* Help Link */}
          <div style={{ marginTop: '30px' }}>
            <p style={{ margin: 0, fontSize: '13px', color: '#6c757d' }}>
              Questions? Contact us at{' '}
              <a href="mailto:support@epainhealth.com" style={{ color: '#4491ca' }}>
                support@epainhealth.com
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FollowUpSuccess;
